import { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, FileText, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import { useUIStore } from '@/stores/ui-store';

interface ProductFile {
  id: string;
  product_variant_id: string;
  file_key: string;
  file_name: string;
  file_size_bytes: number;
  file_format: string | null;
  mime_type: string;
  display_order: number;
  created_at: string;
}

interface Props {
  variantId: string | undefined; // undefined = variant not yet saved
  variantLabel?: string;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function VariantFileUpload({ variantId, variantLabel }: Props) {
  const [files, setFiles] = useState<ProductFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    if (!variantId) return;
    setIsLoading(true);
    api
      .get<{ files: ProductFile[] }>(`/admin/product-files?variant_id=${variantId}`)
      .then((r) => setFiles(r.files || []))
      .catch(() => setFiles([]))
      .finally(() => setIsLoading(false));
  }, [variantId]);

  if (!variantId) {
    return (
      <div className="text-sm text-stone-500 italic mt-2">
        Save the variant first, then upload files here.
      </div>
    );
  }

  const handleUpload = async (filesToUpload: FileList | null) => {
    if (!filesToUpload || filesToUpload.length === 0) return;
    setIsUploading(true);
    try {
      for (const f of Array.from(filesToUpload)) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('variant_id', variantId);
        const res = await api.upload<{ file: ProductFile }>(
          '/admin/product-files',
          fd,
        );
        setFiles((prev) => [...prev, res.file]);
      }
      addToast(`Uploaded ${filesToUpload.length} file(s)`, 'success');
    } catch (err: any) {
      addToast(err?.message || 'Upload failed', 'error');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId: string) => {
    if (!confirm('Delete this file? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/product-files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      addToast('File deleted', 'success');
    } catch (err: any) {
      const msg = err?.message || 'Delete failed';
      addToast(msg, 'error');
    }
  };

  return (
    <div className="border border-stone-200 rounded-lg p-4 mt-3 bg-stone-50">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-stone-700">
          Files {variantLabel ? `for ${variantLabel}` : ''}
        </h4>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {isUploading ? 'Uploading…' : 'Upload Files'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>
      {isLoading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-stone-500">
          No files attached. Upload PDFs, spreadsheets, or zips that customers will receive after purchase.
        </p>
      ) : (
        <ul className="divide-y divide-stone-200">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-stone-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{f.file_name}</p>
                  <p className="text-xs text-stone-500">
                    {(f.file_format || '').toUpperCase()} · {fmtSize(f.file_size_bytes)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(f.id)}
                className="text-stone-400 hover:text-red-600 transition-colors p-1"
                title="Delete file"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
